import React from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { ChevronLeft, Lock, CheckCircle2, Moon, Gamepad2, Sparkles, ArrowRight } from 'lucide-react';
import { useLab, studentBase, getCourseTemplateForChild, resolveLabChildId } from '../LabContext';
import { LabLayout, Card, ParentPreviewBadge } from '../LabUI';
import { computeDayStatus, getWeeksForCourse } from '../data/lessons';

// Visual Week 10 quest map:
// 5 mission nodes + Night Game portal connected by a glowing curved path.
// Course-aware labels: Max keeps the original money/bank/world/ideas/gate
// theming; Anita uses the five quest "огоньки" (choice / safety / explorer
// / map+plan / final author gate).
// Locked until Week 9 Day 5 is completed (= the moment Week 10 Day 1 unlocks).
// Pure CSS/SVG — no extra deps. Cozy nighttime, golden glowing path.

// ---------- Course-aware node config ----------
const MAX_NODES = [
  { id: 'day-1', short: { ru: 'Деньги',  en: 'Money' },       full: { ru: 'Огонёк денег',     en: 'Money Light' },        IconCmp: IconChest },
  { id: 'day-2', short: { ru: 'Банк',    en: 'Bank' },        full: { ru: 'Огонёк банка',     en: 'Bank Light' },         IconCmp: IconBank },
  { id: 'day-3', short: { ru: 'Мир',     en: 'World' },       full: { ru: 'Огонёк мира',      en: 'World Light' },        IconCmp: IconCompass },
  { id: 'day-4', short: { ru: 'Идеи',    en: 'Ideas' },       full: { ru: 'Огонёк идей',      en: 'Ideas Light' },        IconCmp: IconBulb },
  { id: 'day-5', short: { ru: 'Ворота',  en: 'Gate' },        full: { ru: 'Финальные ворота', en: 'Final Gate' },         IconCmp: IconGate }
];

const ANITA_NODES = [
  { id: 'day-1', short: { ru: 'Выбор',           en: 'Choice' },      full: { ru: 'Огонёк выбора',         en: 'Choice Light' },       IconCmp: IconChoice },
  { id: 'day-2', short: { ru: 'Безопасность',    en: 'Safety' },      full: { ru: 'Огонёк безопасности',   en: 'Safety Light' },       IconCmp: IconShield },
  { id: 'day-3', short: { ru: 'Исследователь',   en: 'Explorer' },    full: { ru: 'Огонёк исследователя',  en: 'Explorer Light' },     IconCmp: IconExplorer },
  { id: 'day-4', short: { ru: 'Карта и план',    en: 'Map and Plan' },full: { ru: 'Огонёк карты и плана',  en: 'Map and Plan Light' }, IconCmp: IconMapPlan },
  { id: 'day-5', short: { ru: 'Ворота',          en: 'Gate' },        full: { ru: 'Финальные ворота',      en: 'Final Gate' },         IconCmp: IconGate }
];

const PORTAL_LABEL = { ru: 'Ночная игра', en: 'Night Game' };

const Week10QuestMap = () => {
  const { t, tx, user, childProgress } = useLab();
  const params = useParams();
  const location = useLocation();
  // Allow this component to be mounted under both /lab/max (legacy) and
  // /lab/student/:studentId (dynamic). The URL param takes precedence,
  // then the legacy pathname slug, so parent preview never falls back to
  // the wrong child.
  const childId = resolveLabChildId({
    routeStudentId: params.studentId,
    pathname: location.pathname,
    user,
  });
  const base = studentBase(childId);
  const progress = childProgress(childId);
  const isParent = user?.role === 'parent' || user?.role === 'super_admin';
  const courseTemplate = getCourseTemplateForChild(user, childId);
  const weeks = getWeeksForCourse(courseTemplate);

  // Quest map itself unlocks only when Week 10 Day 1 is open (= W9D5 completed).
  // Parents / super-admins bypass this gate so they can preview the full map.
  const week10Day1Status = computeDayStatus(progress, 'week-10', 'day-1', weeks);
  const mapLocked = !isParent && week10Day1Status === 'locked';
  const day5Status = computeDayStatus(progress, 'week-10', 'day-5', weeks);
  const gameUnlocked = isParent || day5Status === 'completed';

  // Pick course-aware node config (no Max content for Anita, no Anita
  // content for Max). Falls back to Max for unknown templates.
  const NODE_CONFIG = courseTemplate === 'anita' ? ANITA_NODES : MAX_NODES;

  const nodes = NODE_CONFIG.map((n, i) => ({
    ...n,
    index: i + 1,
    status: computeDayStatus(progress, 'week-10', n.id, weeks)
  }));

  // Find current "Nimny is here" node (first non-completed, non-locked).
  const currentIdx = nodes.findIndex(
    (n) => n.status === 'open' || n.status === 'in_progress'
  );
  const completedCount = nodes.filter((n) => n.status === 'completed').length;
  const totalLights = nodes.length;

  if (mapLocked) {
    return (
      <LabLayout>
        <BackLink t={t} base={base} />
        <Card className="mt-6 p-8 text-center" data-testid="quest-map-locked">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-300/10 border border-amber-300/25 flex items-center justify-center text-amber-200/80">
            <Lock className="w-6 h-6" />
          </div>
          <h1
            className="text-2xl text-[#f3d9a4]"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {t('questMapLockedTitle')}
          </h1>
          <p className="mt-2 text-amber-200/70 font-serif text-sm">
            {t('questMapLockedHint')}
          </p>
        </Card>
      </LabLayout>
    );
  }

  // ---------- CTA configuration ----------
  // Primary action below the map: open night game if unlocked, otherwise
  // continue to the current quest node. If nothing is open and Day 5 is
  // not yet completed (rare — parent preview only), fall back to Day 1.
  const ctaNode = currentIdx >= 0 ? nodes[currentIdx] : null;
  const ctaPrimary = gameUnlocked
    ? {
        to: `${base}/nimny-night-run`,
        label: tx({ ru: 'Открыть ночную игру', en: 'Open Night Game' }),
        Icon: Gamepad2,
        testId: 'quest-map-cta-night-game'
      }
    : ctaNode
    ? {
        to: `${base}/week-10/${ctaNode.id}`,
        label: tx({
          ru: `Продолжить — ${ctaNode.short.ru}`,
          en: `Continue — ${ctaNode.short.en}`
        }),
        Icon: ArrowRight,
        testId: 'quest-map-cta-continue'
      }
    : null;

  const nextHint = completedCount === totalLights
    ? tx({
        ru: 'Все огоньки собраны! Ночная игра открыта.',
        en: 'All lights collected! The night game is open.'
      })
    : ctaNode
    ? tx({
        ru: `Готов(а) собрать огонёк: ${ctaNode.full.ru}.`,
        en: `Ready to collect: ${ctaNode.full.en}.`
      })
    : tx({
        ru: 'Сначала пройди предыдущий день, чтобы открыть следующий огонёк.',
        en: 'Complete the previous day to unlock the next light.'
      });

  return (
    <LabLayout contained={false}>
      <div className="px-4 sm:px-6 max-w-[860px] mx-auto pb-16">
        <BackLink t={t} base={base} />

        <section className="text-center mt-3">
          <p
            className="inline-flex items-center gap-1.5 text-amber-200/70 text-xs font-sans uppercase tracking-wider"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            <Moon className="w-4 h-4" /> {t('weekLabel')} 10
          </p>
          <h1
            className="mt-1 text-3xl sm:text-4xl text-[#f3d9a4] leading-tight"
            style={{ fontFamily: 'var(--font-heading)' }}
            data-testid="quest-map-title"
          >
            {t('questMapTitle')}
          </h1>
          <p className="mt-2 text-amber-200/70 font-serif text-sm">
            {t('questMapSubtitle')}
          </p>
        </section>

        {/* The map */}
        <div
          className="quest-map mt-6 relative rounded-3xl overflow-hidden border border-amber-300/15"
          data-testid="quest-map"
          aria-label={tx({ ru: 'Карта финального квеста', en: 'Final quest map' })}
        >
          <QuestSky />
          <QuestSparkles />
          {/* curved path (decorative, behind nodes) */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox="0 0 800 420"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="qmPath" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#f3d9a4" stopOpacity="0.85" />
                <stop offset="100%" stopColor="#f3d9a4" stopOpacity="0.55" />
              </linearGradient>
              <filter id="qmGlow">
                <feGaussianBlur stdDeviation="3" />
              </filter>
            </defs>
            <path
              d="M 40 300 Q 160 220 240 280 T 440 240 T 640 290 T 760 220"
              stroke="url(#qmPath)"
              strokeWidth="6"
              strokeLinecap="round"
              fill="none"
              filter="url(#qmGlow)"
              opacity="0.55"
            />
            <path
              d="M 40 300 Q 160 220 240 280 T 440 240 T 640 290 T 760 220"
              stroke="#f3d9a4"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray="2 8"
              fill="none"
              opacity="0.95"
            />
          </svg>

          {/* Nodes row */}
          <div className="relative grid grid-cols-3 sm:grid-cols-6 gap-3 sm:gap-4 p-5 sm:p-7 pt-8 sm:pt-10">
            {nodes.map((node, i) => (
              <QuestNode
                key={node.id}
                node={node}
                t={t}
                tx={tx}
                base={base}
                isCurrent={i === currentIdx}
                isParent={isParent}
              />
            ))}
            <QuestPortal
              unlocked={gameUnlocked}
              base={base}
              t={t}
              tx={tx}
            />
          </div>
        </div>

        <ParentPreviewBadge />

        {/* Progress + next-action hint card */}
        <div
          className="mt-5 rounded-2xl border border-amber-300/20 bg-[#0a1230]/60 backdrop-blur-sm px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
          data-testid="quest-map-progress"
        >
          <div className="flex items-center gap-2 text-amber-100/90 font-sans">
            <Sparkles className="w-4 h-4 text-[#f6c64a]" aria-hidden="true" />
            <span
              className="text-sm sm:text-base"
              style={{ fontFamily: 'var(--font-heading)' }}
              data-testid="quest-map-progress-count"
            >
              {tx({
                ru: `Собрано огоньков: ${completedCount} из ${totalLights}`,
                en: `Lights collected: ${completedCount} of ${totalLights}`
              })}
            </span>
          </div>
          <p
            className="text-amber-200/70 font-serif text-sm flex-1"
            data-testid="quest-map-next-hint"
          >
            {nextHint}
          </p>
        </div>

        {/* Primary CTA */}
        {ctaPrimary && (
          <div className="mt-4 text-center">
            <Link
              to={ctaPrimary.to}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-[#d4a574] hover:bg-[#c89860] text-[#1a1208] font-sans transition-colors shadow-[0_0_24px_rgba(212,165,116,0.35)]"
              data-testid={ctaPrimary.testId}
            >
              <ctaPrimary.Icon className="w-5 h-5" />
              {ctaPrimary.label}
            </Link>
          </div>
        )}

        {gameUnlocked && (
          <div className="mt-3 text-center">
            <Link
              to={`${base}/nimny-night-run`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-amber-300/30 text-amber-100/80 hover:text-amber-100 hover:border-amber-300/50 font-sans text-sm transition-colors"
              data-testid="quest-map-night-game-cta"
            >
              <Gamepad2 className="w-4 h-4" />
              {t('questOpenNightGame')}
            </Link>
          </div>
        )}
      </div>
    </LabLayout>
  );
};

const BackLink = ({ t, base }) => (
  <Link
    to={`${base}/path`}
    className="inline-flex items-center gap-1.5 text-amber-200/75 hover:text-amber-200 text-sm font-sans"
    data-testid="quest-map-back"
  >
    <ChevronLeft className="w-4 h-4" />
    {t('back')}
  </Link>
);

// Floating sparkles scattered along the magical path. Pure CSS animation.
const QuestSparkles = () => {
  // Pre-computed positions following the path's general curve.
  const spots = [
    { l: '8%',  t: '70%', d: 0    },
    { l: '18%', t: '58%', d: 0.6  },
    { l: '28%', t: '64%', d: 1.2  },
    { l: '38%', t: '52%', d: 0.3  },
    { l: '48%', t: '57%', d: 0.9  },
    { l: '58%', t: '49%', d: 1.5  },
    { l: '68%', t: '63%', d: 0.4  },
    { l: '78%', t: '55%', d: 1.1  },
    { l: '88%', t: '46%', d: 0.7  }
  ];
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      {spots.map((s, i) => (
        <span
          key={i}
          className="quest-sparkle"
          style={{ left: s.l, top: s.t, animationDelay: `${s.d}s` }}
        />
      ))}
    </div>
  );
};

// Inline starry sky background — no external image required.
const QuestSky = () => (
  <div
    className="absolute inset-0"
    aria-hidden="true"
    style={{
      background:
        'radial-gradient(ellipse at 80% 20%, rgba(243,217,164,0.18), transparent 55%), radial-gradient(ellipse at 20% 80%, rgba(120,160,255,0.10), transparent 60%), linear-gradient(180deg, #06102a 0%, #0a1740 55%, #0a1230 100%)'
    }}
  >
    {/* moon */}
    <div
      className="absolute"
      style={{
        top: 14,
        right: 14,
        width: 38,
        height: 38,
        borderRadius: '50%',
        background:
          'radial-gradient(circle at 35% 35%, #fff8e1 0%, #f0d97c 55%, #c9a44a 100%)',
        boxShadow: '0 0 28px rgba(243,217,164,0.45)'
      }}
    />
    {/* stars */}
    {Array.from({ length: 36 }).map((_, i) => {
      const top = (i * 53) % 95;
      const left = (i * 37) % 98;
      const size = (i % 3) + 1;
      const op = 0.4 + ((i * 7) % 5) * 0.1;
      return (
        <span
          key={i}
          className="absolute rounded-full bg-amber-100"
          style={{
            top: `${top}%`,
            left: `${left}%`,
            width: size,
            height: size,
            opacity: op,
            boxShadow: '0 0 6px rgba(255,240,200,0.7)'
          }}
        />
      );
    })}
  </div>
);

const QuestNode = ({ node, tx, base, isCurrent, isParent }) => {
  const completed = node.status === 'completed';
  const locked = node.status === 'locked';
  // Parent / super-admin can click any node regardless of lock state.
  const clickable = !locked || isParent;
  const stateClass = completed
    ? 'completed'
    : locked
    ? 'locked'
    : isCurrent
    ? 'current'
    : 'open';
  const ariaLabel = tx({
    ru: `${node.full.ru} — ${
      completed ? 'собрано' : locked ? 'закрыто' : isCurrent ? 'текущий огонёк' : 'открыто'
    }`,
    en: `${node.full.en} — ${
      completed ? 'collected' : locked ? 'locked' : isCurrent ? 'current light' : 'open'
    }`
  });
  const inner = (
    <div
      className={`quest-node quest-node--${stateClass}`}
      data-testid={`quest-node-${node.id}`}
      data-status={node.status}
    >
      <div className="quest-node__circle">
        {completed ? (
          <CheckCircle2 className="w-5 h-5" />
        ) : locked ? (
          <Lock className="w-4 h-4" />
        ) : node.IconCmp ? (
          <span className="quest-node__glyph"><node.IconCmp /></span>
        ) : (
          <span className="quest-node__num">{node.index}</span>
        )}
        <span className="quest-node__index" aria-hidden="true">{node.index}</span>
        {isCurrent && <NimnyMarker tx={tx} />}
      </div>
      <div className="quest-node__label">
        <div className="quest-node__short" style={{ fontFamily: 'var(--font-heading)' }}>
          {tx(node.short)}
        </div>
        <div className="quest-node__full">{tx(node.full)}</div>
      </div>
    </div>
  );
  if (!clickable) {
    return (
      <div
        className="quest-node-wrap"
        aria-disabled="true"
        aria-label={ariaLabel}
      >
        {inner}
      </div>
    );
  }
  return (
    <Link
      to={`${base}/week-10/${node.id}`}
      className="quest-node-wrap"
      data-testid={`quest-node-link-${node.id}`}
      aria-label={ariaLabel}
    >
      {inner}
    </Link>
  );
};

const QuestPortal = ({ unlocked, base, t, tx }) => {
  const ariaLabel = tx({
    ru: `${PORTAL_LABEL.ru} — ${unlocked ? 'открыта' : 'закрыта'}`,
    en: `${PORTAL_LABEL.en} — ${unlocked ? 'unlocked' : 'locked'}`
  });
  const inner = (
    <div
      className={`quest-portal ${unlocked ? 'quest-portal--unlocked' : 'quest-portal--locked'}`}
      data-testid="quest-portal"
      data-status={unlocked ? 'unlocked' : 'locked'}
    >
      <div className="quest-portal__arch">
        {unlocked ? <IconPortalGate /> : <Lock className="w-5 h-5" />}
        {unlocked && <span className="quest-portal__sparkle" aria-hidden="true" />}
      </div>
      <div className="quest-portal__label">
        <div className="quest-portal__title" style={{ fontFamily: 'var(--font-heading)' }}>
          {tx(PORTAL_LABEL)}
        </div>
        <div className="quest-portal__hint">
          {unlocked ? t('questPortalUnlockedHint') : t('questPortalLockedHint')}
        </div>
      </div>
    </div>
  );
  if (!unlocked) {
    return (
      <div className="quest-node-wrap" aria-disabled="true" aria-label={ariaLabel}>
        {inner}
      </div>
    );
  }
  return (
    <Link
      to={`${base}/nimny-night-run`}
      className="quest-node-wrap"
      data-testid="quest-portal-link"
      aria-label={ariaLabel}
    >
      {inner}
    </Link>
  );
};

// ---------- Location icons (inline SVG, ~22px) ----------
// Tiny illustrative glyphs for each Week 10 mission node.
function IconChest() {
  return (
    <svg viewBox="0 0 24 22" width="24" height="22" aria-hidden="true">
      <rect x="3" y="9" width="18" height="10" rx="1.5" fill="#a26b2c" stroke="#5d3a17" strokeWidth="1.4" />
      <path d="M3 9 Q3 4 12 4 Q21 4 21 9" fill="#c9892e" stroke="#5d3a17" strokeWidth="1.4" />
      <rect x="10.5" y="11.5" width="3" height="4" rx="0.5" fill="#f6c64a" stroke="#5d3a17" strokeWidth="0.8" />
      <line x1="3" y1="13" x2="21" y2="13" stroke="#5d3a17" strokeWidth="0.8" />
    </svg>
  );
}
function IconBank() {
  return (
    <svg viewBox="0 0 24 22" width="24" height="22" aria-hidden="true">
      <path d="M3 9 L12 3 L21 9 Z" fill="#f3d9a4" stroke="#5d3a17" strokeWidth="1.2" />
      <rect x="4.5" y="10" width="2" height="7" fill="#f3d9a4" stroke="#5d3a17" strokeWidth="1" />
      <rect x="9" y="10" width="2" height="7" fill="#f3d9a4" stroke="#5d3a17" strokeWidth="1" />
      <rect x="13" y="10" width="2" height="7" fill="#f3d9a4" stroke="#5d3a17" strokeWidth="1" />
      <rect x="17.5" y="10" width="2" height="7" fill="#f3d9a4" stroke="#5d3a17" strokeWidth="1" />
      <rect x="3" y="17" width="18" height="2" fill="#f3d9a4" stroke="#5d3a17" strokeWidth="1" />
    </svg>
  );
}
function IconCompass() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="#1f2a55" stroke="#f3d9a4" strokeWidth="1.4" />
      <circle cx="12" cy="12" r="1.6" fill="#f3d9a4" />
      <polygon points="12,4 14,12 12,11 10,12" fill="#f6c64a" />
      <polygon points="12,20 10,12 12,13 14,12" fill="#f3d9a4" opacity="0.6" />
    </svg>
  );
}
function IconBulb() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="24" aria-hidden="true">
      <path d="M12 3 Q5 4 6 11 Q6 14 9 16 L9 18 L15 18 L15 16 Q18 14 18 11 Q19 4 12 3 Z" fill="#f6c64a" stroke="#7a4a1c" strokeWidth="1.2" />
      <rect x="9" y="18" width="6" height="2" fill="#7a4a1c" />
      <rect x="10" y="20" width="4" height="2" rx="0.8" fill="#7a4a1c" />
      <path d="M11 9 L13 13 M13 9 L11 13" stroke="#fff8e1" strokeWidth="0.8" />
    </svg>
  );
}
function IconGate() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path d="M4 22 L4 11 Q4 4 12 4 Q20 4 20 11 L20 22" fill="#1f2a55" stroke="#f3d9a4" strokeWidth="1.5" />
      <path d="M7 22 L7 12 Q7 7 12 7 Q17 7 17 12 L17 22" fill="#0e1a3c" stroke="#f6c64a" strokeWidth="1.2" />
      <circle cx="12" cy="13" r="1.4" fill="#f6c64a" />
      <line x1="12" y1="14" x2="12" y2="22" stroke="#f6c64a" strokeWidth="1" />
    </svg>
  );
}

// Anita-specific icons — match the five quest lights.
function IconChoice() {
  // Two diverging paths meeting at a glowing dot — "выбор".
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <circle cx="12" cy="6" r="2.2" fill="#f6c64a" stroke="#7a4a1c" strokeWidth="1" />
      <path d="M12 8 L6 20" stroke="#f3d9a4" strokeWidth="2" strokeLinecap="round" fill="none" />
      <path d="M12 8 L18 20" stroke="#f3d9a4" strokeWidth="2" strokeLinecap="round" fill="none" />
      <circle cx="6" cy="20" r="1.6" fill="#f3d9a4" />
      <circle cx="18" cy="20" r="1.6" fill="#f3d9a4" />
    </svg>
  );
}
function IconShield() {
  // Shield with a small check inside — "безопасность".
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path d="M12 3 L20 6 L20 12 Q20 18 12 21 Q4 18 4 12 L4 6 Z" fill="#1f2a55" stroke="#f3d9a4" strokeWidth="1.4" />
      <path d="M8.5 12.5 L11 15 L15.5 9.5" stroke="#f6c64a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}
function IconExplorer() {
  // Magnifier glass over a leaf — observation in nature.
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path d="M6 17 Q6 11 12 11 Q14 8 18 9 Q17 13 14 15 Q12 17 6 17 Z" fill="#2f5a2a" stroke="#a7d18c" strokeWidth="1.1" />
      <circle cx="10" cy="10" r="4" fill="none" stroke="#f3d9a4" strokeWidth="1.6" />
      <line x1="12.8" y1="12.8" x2="17" y2="17" stroke="#f3d9a4" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
function IconMapPlan() {
  // Folded map with a flag pin — "карта и план".
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path d="M3 6 L9 4 L15 6 L21 4 L21 18 L15 20 L9 18 L3 20 Z" fill="#f3d9a4" stroke="#5d3a17" strokeWidth="1.2" />
      <line x1="9" y1="4" x2="9" y2="18" stroke="#5d3a17" strokeWidth="0.8" strokeDasharray="1.5 1.5" />
      <line x1="15" y1="6" x2="15" y2="20" stroke="#5d3a17" strokeWidth="0.8" strokeDasharray="1.5 1.5" />
      <line x1="13" y1="13" x2="13" y2="8" stroke="#7a4a1c" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M13 8 L17 9.5 L13 11 Z" fill="#d24c4c" stroke="#7a4a1c" strokeWidth="0.8" />
    </svg>
  );
}

function IconPortalGate() {
  return (
    <svg viewBox="0 0 36 36" width="34" height="34" aria-hidden="true">
      <defs>
        <radialGradient id="qpGate" cx="50%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#fff4c2" />
          <stop offset="60%" stopColor="#f6c64a" />
          <stop offset="100%" stopColor="#a96c20" />
        </radialGradient>
      </defs>
      <path d="M5 32 L5 16 Q5 5 18 5 Q31 5 31 16 L31 32" fill="url(#qpGate)" stroke="#f3d9a4" strokeWidth="1.4" />
      <path d="M9 32 L9 18 Q9 9 18 9 Q27 9 27 18 L27 32" fill="#1a2247" stroke="#f6c64a" strokeWidth="1.2" />
      <circle cx="18" cy="18" r="2.2" fill="#f6c64a" />
      <line x1="18" y1="20" x2="18" y2="32" stroke="#f6c64a" strokeWidth="1.4" />
      <polygon points="18,2 19,6 23,7 19,8 18,12 17,8 13,7 17,6" fill="#f6c64a" />
    </svg>
  );
}

// Tiny inline-SVG Nimny marker — small elf girl with lantern.
// Positioned just above the current node circle.
const NimnyMarker = ({ tx }) => {
  const label = tx({ ru: 'Нимни здесь', en: 'Nimny is here' });
  return (
    <span
      className="quest-nimny-marker"
      aria-label={label}
      title={label}
      data-testid="quest-nimny-marker"
    >
      <svg viewBox="0 0 48 56" width="34" height="40" aria-hidden="true">
        {/* hair back */}
        <path d="M9 30 Q9 12 24 10 Q39 12 39 30 L36 42 L12 42 Z" fill="#f3d9a4" />
        {/* face */}
        <ellipse cx="24" cy="26" rx="11" ry="11" fill="#fce6c5" />
        {/* pointed ears */}
        <path d="M13 24 L9 22 L13 28 Z" fill="#fce6c5" />
        <path d="M35 24 L39 22 L35 28 Z" fill="#fce6c5" />
        {/* eyes */}
        <circle cx="20" cy="26" r="1.4" fill="#3a2a14" />
        <circle cx="28" cy="26" r="1.4" fill="#3a2a14" />
        {/* smile */}
        <path d="M21 30 Q24 32 27 30" stroke="#7a4a1c" strokeWidth="1" fill="none" strokeLinecap="round" />
        {/* hair front strands */}
        <path d="M14 18 Q20 14 24 18" stroke="#e6c178" strokeWidth="2" fill="none" strokeLinecap="round" />
        <path d="M34 18 Q28 14 24 18" stroke="#e6c178" strokeWidth="2" fill="none" strokeLinecap="round" />
        {/* body / dress */}
        <path d="M14 38 Q24 36 34 38 L36 50 L12 50 Z" fill="#f1e4cf" />
        {/* belt */}
        <rect x="14" y="42" width="20" height="2" fill="#b9905a" />
        {/* lantern */}
        <circle cx="38" cy="40" r="3.5" fill="#f6c64a" />
        <circle cx="38" cy="40" r="6" fill="#f6c64a" opacity="0.25" />
        <line x1="38" y1="34" x2="38" y2="36" stroke="#7a4a1c" strokeWidth="1" />
      </svg>
    </span>
  );
};

export default Week10QuestMap;
